import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["initials", "avatarUrl", "isOpen", "aria-readonly", "aria-owns", "aria-expanded"];
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'foundations-theming/utils';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/transitional/components/View';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Flex } from 'foundations-components/components/Flex';
import { Image } from 'foundations-components/transitional/components/Image';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { NotificationIcon } from 'foundations-assets/icons/components/NotificationIcon';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Link } from 'foundations-components/transitional/components/Link';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { HR } from 'foundations-components/transitional/components/HR';
import { LogoutIcon } from 'foundations-assets/icons/components/LogoutIcon';
import { SettingsIcon } from 'foundations-assets/icons/components/SettingsIcon';
import { SettingsPanel } from './SettingsPanel';
import { NotificationsPanel } from './NotificationsPanel';
import { useLoginVerify } from '../hooks/useLoginVerify';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationBadge } from 'foundations-components/transitional/components/NotificationBadge';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { getIsThemePreview } from '../utils/metadata';
import { useIsPillBorderRadius } from '../hooks/useIsPillBorderRadius';
import { useIsUngated } from '../hooks/useIsUngated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const NOTIFICATIONS_GATE = 'PortalsPlatform::Buyer::Notifications';
const HoverContainer = styled.div.withConfig({
  displayName: "MyAccountPopover__HoverContainer"
})(({
  theme,
  $isPill
}) => ({
  borderRadius: $isPill ? theme.borderRadius['0'] : theme.borderRadius['100'],
  '&:hover': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));
const RotatingIcon = styled.span.withConfig({
  displayName: "MyAccountPopover__RotatingIcon"
})(({
  $isOpen
}) => ({
  display: 'inline-flex',
  transition: 'transform 150ms ease-in-out',
  transform: $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
}));
function UserAvatar({
  initials,
  avatarUrl,
  size = 'medium'
}) {
  const dimensions = size === 'small' ? 32 : 40;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);
  const showImage = Boolean(avatarUrl) && !imageFailed;
  if (showImage) {
    return /*#__PURE__*/_jsx(Flex, {
      "aria-hidden": "true",
      inlineSize: `${dimensions}px`,
      blockSize: `${dimensions}px`,
      flexShrink: 0,
      borderRadius: "full",
      borderWidth: "100",
      borderStyle: "solid",
      borderColor: "border-core-default",
      overflow: "hidden",
      children: /*#__PURE__*/_jsx(Image, {
        src: avatarUrl,
        alt: "",
        responsive: false,
        inlineSize: "100%",
        blockSize: "100%",
        objectFit: "cover",
        onError: () => setImageFailed(true)
      })
    });
  }
  return /*#__PURE__*/_jsx(Flex, {
    "aria-hidden": "true",
    backgroundColor: "fill-accent-teal-default",
    borderRadius: "full",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-default",
    inlineSize: `${dimensions}px`,
    blockSize: `${dimensions}px`,
    alignItems: "center",
    justifyContent: "center",
    children: /*#__PURE__*/_jsx(Paragraph, {
      color: "text-inverse-default",
      marginBlock: "0",
      typography: size === 'small' ? 'detail-100' : 'body-100',
      children: initials
    })
  });
}
function AccountButton(_ref) {
  let {
      initials,
      avatarUrl,
      isOpen
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(Button, Object.assign({}, props, {
    use: "unstyled",
    "aria-label": I18n.text('app.myAccountPopover.ariaLabel'),
    "aria-expanded": Boolean(isOpen),
    paddingInline: "0",
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "100",
      children: [/*#__PURE__*/_jsx(UserAvatar, {
        initials: initials,
        avatarUrl: avatarUrl,
        size: "small"
      }), /*#__PURE__*/_jsx(RotatingIcon, {
        $isOpen: Boolean(isOpen),
        "aria-hidden": "true",
        children: /*#__PURE__*/_jsx(DownIcon, {
          size: "100",
          color: "text-core-default"
        })
      })]
    })
  }));
}
function NotificationBell({
  onClick
}) {
  const {
    unreadCount
  } = useNotifications({
    limit: 1
  });
  return /*#__PURE__*/_jsx(NotificationBadge, {
    showBadge: unreadCount > 0,
    badgeLabel: unreadCount > 99 ? '99+' : unreadCount,
    badgeDescription: I18n.text('app.notifications.unreadCount', {
      count: unreadCount
    }),
    placement: "inset",
    children: /*#__PURE__*/_jsx(IconButton, {
      use: "transparent",
      shape: "circle",
      "aria-label": I18n.text('app.notifications.label'),
      "data-test-id": "portals-notification-bell",
      onClick: onClick,
      children: /*#__PURE__*/_jsx(NotificationIcon, {
        size: "500",
        color: "text-core-default"
      })
    })
  });
}
export function MyAccountPopover() {
  const isPill = useIsPillBorderRadius();
  const isThemePreview = getIsThemePreview();
  const isNotificationsEnabled = useIsUngated(NOTIFICATIONS_GATE);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const {
    data: loginVerifyData,
    loading: loginVerifyLoading
  } = useLoginVerify();
  const initials = useMemo(() => {
    if (!loginVerifyData) {
      return 'U';
    }
    const firstName = loginVerifyData.firstname || '';
    const lastName = loginVerifyData.lastname || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  }, [loginVerifyData]);
  const avatarUrl = loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.avatarUrl;
  const renderButton = useCallback(props => /*#__PURE__*/_jsx(AccountButton, Object.assign({}, props, {
    initials: initials,
    avatarUrl: avatarUrl
  })), [initials, avatarUrl]);
  if (loginVerifyLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "extra-small",
      "aria-label": I18n.text('app.myAccountPopover.ariaLoadingLabel')
    });
  }
  if (!loginVerifyData) {
    return /*#__PURE__*/_jsx(Button, {
      use: "primary",
      size: "small",
      href: `/_hcms/mem/login?redirect_url=${encodeURIComponent(window.location.href)}`,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.login"
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      children: [isNotificationsEnabled && /*#__PURE__*/_jsx(NotificationBell, {
        onClick: () => setIsNotificationsOpen(true)
      }), /*#__PURE__*/_jsxs(Dropdown, {
        buttonUse: "unstyled",
        menuWidth: 200,
        placement: "bottom left",
        autoPlacement: false,
        Button: renderButton,
        customButtonProps: {
          isOpen: isDropdownOpen
        },
        open: isDropdownOpen,
        onOpenChange: evt => setIsDropdownOpen(evt.target.value),
        children: [/*#__PURE__*/_jsx(View, {
          paddingBlock: "50",
          paddingInline: "100",
          children: /*#__PURE__*/_jsx(HoverContainer, {
            $isPill: isPill,
            children: /*#__PURE__*/_jsx(Link, {
              use: "unstyled",
              onClick: () => setIsSettingsOpen(true),
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "200",
                padding: "100",
                paddingInlineStart: "200",
                children: [/*#__PURE__*/_jsx(IconHolder, {
                  icon: SettingsIcon,
                  compact: true,
                  color: "text-core-default",
                  size: "400"
                }), /*#__PURE__*/_jsx(Paragraph, {
                  color: "text-core-default",
                  typography: "body-100",
                  marginBlock: "0",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.myAccountPopover.settings.heading"
                  })
                })]
              })
            })
          })
        }), !isThemePreview && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(HR, {
            distance: "flush",
            "aria-hidden": "true"
          }), /*#__PURE__*/_jsx(View, {
            paddingBlock: "100",
            paddingInline: "100",
            children: /*#__PURE__*/_jsx(HoverContainer, {
              $isPill: isPill,
              children: /*#__PURE__*/_jsx(Link, {
                use: "unstyled",
                href: "/_hcms/mem/logout",
                children: /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  gap: "200",
                  padding: "100",
                  paddingInlineStart: "200",
                  children: [/*#__PURE__*/_jsx(IconHolder, {
                    icon: LogoutIcon,
                    compact: true,
                    color: "text-core-default",
                    size: "400"
                  }), /*#__PURE__*/_jsx(Paragraph, {
                    color: "text-core-default",
                    typography: "body-100",
                    marginBlock: "0",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "app.myAccountPopover.logout"
                    })
                  })]
                })
              })
            })
          })]
        })]
      })]
    }), isSettingsOpen && /*#__PURE__*/_jsx(SettingsPanel, {
      onClose: () => setIsSettingsOpen(false)
    }), isNotificationsOpen && /*#__PURE__*/_jsx(NotificationsPanel, {
      onClose: () => setIsNotificationsOpen(false)
    })]
  });
}